'use client';

import { Fragment } from 'react';
import styles from './ExpertPool.module.scss';
import { gaClick } from '@/src/helpers/gtag';
import { useAOS } from '@/src/lib/hooks/useAOS';
import { useInView } from '@/src/lib/hooks/useInView';

interface Expert {
  icon: 'strategy' | 'code' | 'data' | 'product' | 'delivery';
  role: string;
  specialty: string;
}

interface ExpertPoolProps {
  eyebrow?: string;
  title?: string;
  highlightedTitle?: string;
  description?: string;
  experts?: Expert[];
  backgroundImage?: string;
  tilt?: 'left' | 'right';
  id?: string;
}

const ICONS: Record<Expert['icon'], () => JSX.Element> = {
  strategy: () => (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v4" />
      <path d="M12 18v4" />
      <path d="M2 12h4" />
      <path d="M18 12h4" />
    </svg>
  ),
  code: () => (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="16 18 22 12 16 6" />
      <polyline points="8 6 2 12 8 18" />
    </svg>
  ),
  data: () => (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
    </svg>
  ),
  product: () => (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="18" height="18" rx="4" />
      <path d="M3 9h18" />
      <path d="M9 21V9" />
    </svg>
  ),
  delivery: () => (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
};

const DEFAULT_EXPERTS: Expert[] = [
  { icon: 'delivery', role: 'Responsable delivery', specialty: 'Votre interlocuteur unique' },
  { icon: 'strategy', role: 'Agile coach', specialty: 'Roadmap, priorités & valeur métier' },
  { icon: 'code', role: 'Développeur senior', specialty: 'Full-stack & cloud' },
  { icon: 'data', role: 'Data engineer', specialty: 'Pipelines & dashboards' },
  { icon: 'product', role: 'Product owner', specialty: 'Priorisation & valeur métier' },
];

const renderTitle = (text: string) => {
  const parts = text.split('*');
  return parts.map((part, i) =>
    i % 2 === 1 ? <em key={i}>{part}</em> : <Fragment key={i}>{part}</Fragment>
  );
};

export function ExpertPool({
  eyebrow = 'VOS EXPERTS',
  title = 'Une équipe senior',
  highlightedTitle = '*dédiée* à votre projet.',
  description = 'Pas de bench anonyme. Chaque expert est choisi pour ses compétences et intégré à une équipe coordonnée par un responsable delivery.',
  experts = DEFAULT_EXPERTS,
  backgroundImage,
  tilt = 'left',
  id,
}: ExpertPoolProps) {
  useAOS();
  const [poolRef, isInView] = useInView<HTMLDivElement>();

  const tiltClass = tilt === 'left' ? styles.tiltLeft : styles.tiltRight;
  const hasBgClass = backgroundImage ? styles.hasBg : '';

  return (
    <div
      id={id}
      className={`${styles.root} ${tiltClass} ${hasBgClass}`}
      style={backgroundImage ? { backgroundImage: `url(${backgroundImage})` } : undefined}
    >
      <div className={styles.bg} aria-hidden="true" />
      <div className={styles.header} data-aos="fade-up" data-aos-duration="700">
        {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
        <h2 className={styles.title}>
          {renderTitle(title)} {renderTitle(highlightedTitle)}
        </h2>
        {description && <p className={styles.description}>{description}</p>}
      </div>

      <div ref={poolRef} className={styles.pool}>
        {experts.map((expert, i) => {
          const Icon = ICONS[expert.icon];
          const isDelivery = expert.icon === 'delivery';
          return (
            <div
              key={expert.role}
              className={`${styles.card} ${isDelivery ? styles.featuredCard : ''} ${isInView ? styles.visible : ''}`}
              style={{ ['--i' as string]: i } as React.CSSProperties}
              role="button"
              tabIndex={0}
              onClick={() => gaClick('expert_pool_card', { role: expert.role })}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  gaClick('expert_pool_card', { role: expert.role });
                }
              }}
            >
              <div className={styles.icon}>
                <Icon />
              </div>
              <h3 className={styles.role}>{expert.role}</h3>
              <p className={styles.specialty}>{expert.specialty}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
